import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import type { Cliente } from '../../db/tipos'
import { semRota } from '../../clientes/inativacao'
import { EMOJI_CLASSIFICACAO, ROTULO_CLASSIFICACAO, CLASSIFICACOES } from '../../clientes/classificacao'
import {
  comprasPorCliente,
  comQueda,
  fraseDaSaude,
  saudeDaCarteira,
  prazoDoCliente,
  ROTULO_SITUACAO,
  semVisitaRecente,
  situacaoDeCompra,
  type Compra,
  type SituacaoDeCompra,
} from '../../clientes/carteira'
import { dataISOLocal } from '../../pedidos/precos'
import { formatarMoeda } from '../../lib/formatos'
import { Icone, type NomeIcone } from './IconesCarteira'

// ▶ Central da Carteira (print dela + decisoes de 08/10, Etapas 1-9 no bloco de
// notas). As regras moram em clientes/carteira.ts; aqui so se desenha.
// A faixa ambar "N clientes sem rota" virou o card "Sem rota definida" (V2):
// mesma conta da carteira inteira, mesmo toque que filtra.

type Grupo = 'todos' | SituacaoDeCompra | 'queda' | 'sem_rota' | 'sem_visita' | 'atencao'
type Ordem = 'nome' | 'ultima_compra' | 'situacao'

type Linha = {
  cliente: Cliente
  compras: Compra[]
  situacao: SituacaoDeCompra
  queda: boolean
  semVisita: boolean
  semRota: boolean
}

const COR_SELO: Record<SituacaoDeCompra, string> = {
  comprando: 'bg-emerald-50 text-emerald-700',
  em_risco: 'bg-amber-50 text-amber-700',
  esquecido: 'bg-red-50 text-red-700',
  sem_compra: 'bg-slate-100 text-slate-600',
}

const ICONE_SELO: Record<SituacaoDeCompra, string> = {
  comprando: '✓',
  em_risco: '⚠',
  esquecido: '⚠',
  sem_compra: '–',
}

// V1: o circulo do card e a classificacao (simbolo + cor). Sem classificacao,
// um "?" em cinza, para lembrar que falta classificar (pedido dela, 08/10).
const FUNDO_CLASSIFICACAO: Record<NonNullable<Cliente['potencial']>, string> = {
  A: 'bg-amber-100',
  B: 'bg-slate-200',
  C: 'bg-orange-200',
  D: 'bg-zinc-100',
}

// Esquecido primeiro: e o que pede acao.
const PESO_SITUACAO: Record<SituacaoDeCompra, number> = { esquecido: 0, em_risco: 1, comprando: 2, sem_compra: 3 }

function dataBR(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${a}`
}

export default function ListaClientes() {
  const [busca, setBusca] = useState('')
  const [grupo, setGrupo] = useState<Grupo>('todos')
  const [ordem, setOrdem] = useState<Ordem>('nome')
  const [fornecedora, setFornecedora] = useState('')
  const [classificacao, setClassificacao] = useState('')
  const [cadastro, setCadastro] = useState<'ativo' | 'inativo'>('ativo')
  const [explicando, setExplicando] = useState(false)

  const representadas = useLiveQuery(
    () => banco.representadas.filter((r) => r.apagado_em === null).sortBy('nome'),
    [],
  )

  // Tudo de uma vez e calculado no aparelho: funciona sem sinal, e a carteira
  // maior medida (152 clientes, 51 pedidos) e conta instantanea.
  const dados = useLiveQuery(async () => {
    const [clientes, vinculos, pedidos, subPedidos, itens, visitas] = await Promise.all([
      banco.clientes.filter((c) => c.apagado_em === null).toArray(),
      banco.clientes_representadas.filter((v) => v.apagado_em === null).toArray(),
      banco.pedidos.toArray(),
      banco.sub_pedidos.toArray(),
      banco.itens_pedido.toArray(),
      banco.visitas.filter((v) => v.apagado_em === null).toArray(),
    ])
    return { clientes, vinculos, pedidos, subPedidos, itens, visitas }
  }, [])

  const hoje = dataISOLocal(new Date())
  // Pedido dela 08/10: a FORNECEDORA muda a tela inteira (saude, quadros,
  // atencao e chips) -- o vendedor ve o desempenho em cada fornecedora.
  // Classificacao, Cadastro inativo e os chips so mexem na LISTA.
  const linhas: Linha[] = []
  // A carteira ATIVA da fornecedora escolhida (ou toda): base do topo e dos chips.
  const carteira: Linha[] = []
  // Contagem dos chips de classificacao: ANTES do proprio filtro de
  // classificacao, senao escolher Ouro zeraria Prata, Bronze e Niquel.
  const porClassificacao: Record<string, number> = { A: 0, B: 0, C: 0, D: 0 }
  if (dados) {
    const idsDaFornecedora = fornecedora
      ? new Set(dados.vinculos.filter((v) => v.representada_id === fornecedora).map((v) => v.cliente_id))
      : null
    const compras = comprasPorCliente(dados.pedidos, dados.subPedidos, dados.itens, fornecedora || null)
    // O dia da visita e o do RELOGIO dela: data_hora e UTC (ver visitasDeHoje).
    const ultimaVisita = new Map<string, string>()
    for (const v of dados.visitas) {
      const dia = dataISOLocal(new Date(v.data_hora))
      const atual = ultimaVisita.get(v.cliente_id)
      if (!atual || dia > atual) ultimaVisita.set(v.cliente_id, dia)
    }
    const linhaDe = (c: Cliente, mapa: Map<string, Compra[]>): Linha => {
      const prazo = prazoDoCliente(c)
      const doCliente = mapa.get(c.id) ?? []
      return {
        cliente: c,
        compras: doCliente,
        situacao: situacaoDeCompra(doCliente, prazo, hoje),
        queda: comQueda(doCliente),
        semVisita: semVisitaRecente(ultimaVisita.get(c.id) ?? null, prazo, hoje),
        semRota: semRota(c),
      }
    }
    for (const c of dados.clientes) {
      if (idsDaFornecedora && !idsDaFornecedora.has(c.id)) continue
      const linha = linhaDe(c, compras)
      // E2-4: o inativo de cadastro fica fora do topo e dos chips.
      if (c.status === 'ativo') carteira.push(linha)
      if (c.status !== cadastro) continue
      if (c.potencial) porClassificacao[c.potencial] += 1
      if (classificacao && c.potencial !== classificacao) continue
      linhas.push(linha)
    }
  }

  function contar(lista: Linha[]) {
    return {
      todos: lista.length,
      comprando: lista.filter((l) => l.situacao === 'comprando').length,
      em_risco: lista.filter((l) => l.situacao === 'em_risco').length,
      esquecido: lista.filter((l) => l.situacao === 'esquecido').length,
      sem_compra: lista.filter((l) => l.situacao === 'sem_compra').length,
      queda: lista.filter((l) => l.queda).length,
      sem_rota: lista.filter((l) => l.semRota).length,
      sem_visita: lista.filter((l) => l.semVisita).length,
    }
  }
  const contaTopo = contar(carteira)
  const conta = contaTopo

  const termo = busca.trim().toLowerCase()
  const digitos = termo.replace(/\D/g, '')
  const visiveis = linhas
    .filter((l) => {
      // P4: Ver todos = quem esta em pelo menos um dos quatro cards.
      if (grupo === 'atencao') return l.situacao === 'em_risco' || l.queda || l.semRota || l.semVisita
      if (grupo === 'queda') return l.queda
      if (grupo === 'sem_rota') return l.semRota
      if (grupo === 'sem_visita') return l.semVisita
      return grupo === 'todos' || l.situacao === grupo
    })
    .filter((l) => {
      if (termo === '') return true
      const c = l.cliente
      return (
        c.razao_social.toLowerCase().includes(termo) ||
        (c.nome_fantasia ?? '').toLowerCase().includes(termo) ||
        (c.municipio ?? '').toLowerCase().includes(termo) ||
        // Busca sem digito nao compara CNPJ: ''.includes('') casaria todos.
        (digitos !== '' && (c.cnpj_cpf ?? '').replace(/\D/g, '').includes(digitos))
      )
    })
    .sort((a, b) => {
      if (ordem === 'ultima_compra') {
        const da = a.compras[0]?.data ?? ''
        const db = b.compras[0]?.data ?? ''
        if (da !== db) return db.localeCompare(da)
      }
      if (ordem === 'situacao' && a.situacao !== b.situacao) {
        return PESO_SITUACAO[a.situacao] - PESO_SITUACAO[b.situacao]
      }
      return a.cliente.razao_social.localeCompare(b.cliente.razao_social, 'pt-BR')
    })

  function alternar(novo: Grupo) {
    setGrupo((atual) => (atual === novo ? 'todos' : novo))
  }

  const nota = saudeDaCarteira(contaTopo)
  const corDoAnel =
    nota === null ? 'text-slate-300' : nota >= 60 ? 'text-emerald-600' : nota >= 40 ? 'text-amber-500' : 'text-red-600'

  // P2 (a): no QUADRO, Ativos = Comprando e Inativos = Esquecidos. O selo do
  // card continua dizendo "Esquecido"; o Inativo de cadastro mora no filtro.
  const quadros: { grupo: Grupo; rotulo: string; valor: number; icone: NomeIcone; cor: string }[] = [
    { grupo: 'todos', rotulo: 'Total', valor: contaTopo.todos, icone: 'pessoas', cor: 'bg-sky-50 text-sky-600' },
    { grupo: 'comprando', rotulo: 'Ativos', valor: contaTopo.comprando, icone: 'check', cor: 'bg-emerald-50 text-emerald-600' },
    { grupo: 'esquecido', rotulo: 'Inativos', valor: contaTopo.esquecido, icone: 'pessoaX', cor: 'bg-red-50 text-red-600' },
    { grupo: 'em_risco', rotulo: 'Em risco', valor: contaTopo.em_risco, icone: 'alerta', cor: 'bg-amber-50 text-amber-500' },
  ]

  // P3: os quatro cards do print. Card com zero some: alerta que nunca zera
  // vira alerta que se aprende a ignorar.
  const atencao: { grupo: Grupo; rotulo: [string, string]; valor: number; icone: NomeIcone; estilo: string; testId?: string }[] = [
    { grupo: 'em_risco', rotulo: ['Em risco de', 'parar de comprar'], valor: contaTopo.em_risco, icone: 'alerta', estilo: 'bg-red-50 text-red-600' },
    { grupo: 'queda', rotulo: ['Com queda', 'nas compras'], valor: contaTopo.queda, icone: 'queda', estilo: 'bg-orange-50 text-orange-600' },
    {
      grupo: 'sem_rota', rotulo: ['Sem rota', 'definida'], valor: contaTopo.sem_rota, icone: 'pino',
      estilo: 'bg-amber-50 text-amber-600', testId: 'faixa-sem-rota',
    },
    { grupo: 'sem_visita', rotulo: ['Sem visita', 'recente'], valor: contaTopo.sem_visita, icone: 'calendario', estilo: 'bg-slate-100 text-slate-700' },
  ]
  const atencaoVisivel = atencao.filter((a) => a.valor > 0)

  const chips: { grupo: Grupo; rotulo: string; valor: number }[] = [
    { grupo: 'todos', rotulo: 'Todos', valor: conta.todos },
    { grupo: 'comprando', rotulo: 'Comprando', valor: conta.comprando },
    { grupo: 'em_risco', rotulo: 'Em risco', valor: conta.em_risco },
    { grupo: 'esquecido', rotulo: 'Esquecidos', valor: conta.esquecido },
    { grupo: 'sem_compra', rotulo: 'Sem compra', valor: conta.sem_compra },
  ]

  return (
    <section className="p-4 space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-widest text-slate-500">MINHA CARTEIRA</p>
          <h2 className="text-2xl font-bold text-slate-900">Clientes</h2>
        </div>
        <Link to="/clientes/novo" className="rounded-xl bg-[#0B4A47] px-4 py-2.5 text-sm font-semibold text-white">
          + Novo
        </Link>
      </div>

      {/* A fornecedora muda a TELA INTEIRA (pedido dela 08/10), por isso mora no
          topo: la embaixo, quem rola ate a lista esqueceria que ela esta ligada. */}
      <label className="block text-xs text-slate-500">
        Fornecedora
        <select
          value={fornecedora}
          onChange={(e) => setFornecedora(e.target.value)}
          className={`mt-1 w-full rounded-xl border px-3 py-2.5 text-sm text-slate-800 ${
            fornecedora ? 'border-[#0B4A47] bg-teal-50 font-semibold' : 'border-slate-300 bg-white'
          }`}
        >
          <option value="">Todas as fornecedoras</option>
          {representadas?.map((r) => (
            <option key={r.id} value={r.id}>{r.nome}</option>
          ))}
        </select>
      </label>

      <div className="flex gap-3 rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 to-white p-3 shadow-sm">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-sm font-semibold text-slate-800">
            Saúde da carteira
            <button
              type="button"
              aria-label="Como a saúde é calculada"
              aria-expanded={explicando}
              onClick={() => setExplicando((v) => !v)}
              className="text-slate-400"
            >
              ⓘ
            </button>
          </p>
          {/* Pedido dela (08/10): "54/100 e o mesmo que 54%" -- anel com a
              porcentagem dentro, no lugar da barra deitada. */}
          <div className="relative mx-auto mt-1.5 h-20 w-20">
            <svg viewBox="0 0 36 36" className={`anel-3d h-20 w-20 -rotate-90 ${corDoAnel}`} aria-hidden>
              <circle cx="18" cy="18" r="15.9155" fill="none" className="stroke-slate-200" strokeWidth="3.2" />
              <circle
                cx="18" cy="18" r="15.9155" fill="none" stroke="currentColor" strokeWidth="3.2"
                strokeLinecap="round" strokeDasharray={`${nota ?? 0} 100`}
              />
            </svg>
            <span className="miolo-3d absolute inset-[17%] rounded-full" aria-hidden />
            <strong className="absolute inset-0 flex items-center justify-center text-xl font-extrabold text-[#0B4A47]">
              {nota === null ? '—' : `${nota}%`}
            </strong>
          </div>
          <p className="mt-1.5 text-xs leading-snug text-slate-600">
            {nota === null ? 'Ainda não há compras para medir.' : fraseDaSaude(nota)}
          </p>
        </div>
        <div className="grid w-[10.5rem] shrink-0 grid-cols-2 gap-1.5 border-l border-slate-200 pl-3">
          {quadros.map((q) => (
            <button
              key={q.grupo}
              type="button"
              aria-pressed={grupo === q.grupo && q.grupo !== 'todos'}
              onClick={() => (q.grupo === 'todos' ? setGrupo('todos') : alternar(q.grupo))}
              className={`flex flex-col items-start gap-0.5 rounded-xl border bg-white p-1.5 text-left shadow-sm ${
                grupo === q.grupo && q.grupo !== 'todos' ? 'border-[#0B4A47] ring-1 ring-[#0B4A47]' : 'border-slate-200'
              }`}
            >
              <span className="flex items-center gap-1">
                <span className={`flex h-6 w-6 items-center justify-center rounded-md ${q.cor}`}>
                  <Icone nome={q.icone} />
                </span>
                <strong className="text-base leading-none text-slate-900">{q.valor}</strong>
              </span>
              <span className="text-[11px] text-slate-600">{q.rotulo}</span>
            </button>
          ))}
        </div>
      </div>

      {explicando && (
        <p className="rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
          A saúde é a parte da carteira que continua comprando: clientes <strong>Ativos</strong> contam inteiro,{' '}
          <strong>Em risco</strong> contam metade e <strong>Inativos</strong> (esquecidos) contam zero. Quem nunca
          comprou fica fora da conta. O prazo de cada cliente: retorno marcado (ex.: 45 dias), rota semanal 15 dias,
          sem rota 30 dias. Passou do prazo = Em risco; passou do dobro = Inativo.
        </p>
      )}

      {atencaoVisivel.length > 0 && (
        <div className="rounded-2xl border border-orange-100 bg-orange-50 p-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-bold text-slate-900">Precisam de atenção</h3>
            <button
              type="button"
              aria-pressed={grupo === 'atencao'}
              onClick={() => alternar('atencao')}
              className={`text-sm ${grupo === 'atencao' ? 'font-semibold text-[#0B4A47]' : 'text-slate-700'}`}
            >
              {grupo === 'atencao' ? 'Limpar ✕' : 'Ver todos ›'}
            </button>
          </div>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pt-1 pb-3">
            {atencaoVisivel.map((a) => (
              <button
                key={a.grupo}
                type="button"
                data-testid={a.testId}
                aria-pressed={grupo === a.grupo}
                onClick={() => alternar(a.grupo)}
                className={`w-[7rem] min-h-[5.5rem] shrink-0 rounded-xl p-2.5 text-left shadow-sm ${a.estilo} ${
                  grupo === a.grupo ? 'ring-2 ring-[#0B4A47]' : ''
                }`}
              >
                <span className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xl font-bold">
                    <Icone nome={a.icone} />
                    {a.valor}
                  </span>
                  <span aria-hidden>{grupo === a.grupo ? '✕' : '›'}</span>
                </span>
                <span className="mt-1 block whitespace-nowrap text-xs leading-snug text-slate-700">
                  {a.rotulo[0]}
                  <br />
                  {a.rotulo[1]}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <input
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="Buscar por nome, CNPJ ou município"
        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5"
      />

      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-3">
        {chips.map((c) => (
          <button
            key={c.grupo}
            type="button"
            aria-pressed={grupo === c.grupo}
            onClick={() => setGrupo(c.grupo)}
            className={`shrink-0 rounded-full border px-3.5 py-1.5 text-sm ${
              grupo === c.grupo ? 'border-[#0B4A47] bg-[#0B4A47] font-semibold text-white' : 'border-slate-300 bg-white text-slate-700'
            }`}
          >
            {c.rotulo} ({c.valor})
          </button>
        ))}
        {/* O ⚙ saiu (pedido dela 08/10). O inativo de CADASTRO continua
            alcancavel por aqui -- nome diferente do quadro "Inativos", que e
            o Esquecido (P2 a). */}
        <button
          type="button"
          aria-pressed={cadastro === 'inativo'}
          onClick={() => setCadastro((atual) => (atual === 'inativo' ? 'ativo' : 'inativo'))}
          className={`shrink-0 rounded-full border px-3.5 py-1.5 text-sm ${
            cadastro === 'inativo' ? 'border-red-700 bg-red-700 font-semibold text-white' : 'border-slate-300 bg-white text-slate-700'
          }`}
        >
          Cadastro inativo{cadastro === 'inativo' ? ' ✕' : ''}
        </button>
      </div>

      {/* Pedido dela (08/10): a classificacao em chips. */}
      <div className="-mx-4 -mt-2 flex gap-2 overflow-x-auto px-4 pb-3">
        {CLASSIFICACOES.map((k) => (
          <button
            key={k}
            type="button"
            aria-pressed={classificacao === k}
            onClick={() => setClassificacao((atual) => (atual === k ? '' : k))}
            className={`shrink-0 rounded-full border px-3 py-1 text-sm ${
              classificacao === k ? 'border-[#0B4A47] bg-teal-50 font-semibold text-[#0B4A47]' : 'border-slate-300 bg-white text-slate-700'
            }`}
          >
            {EMOJI_CLASSIFICACAO[k]} {ROTULO_CLASSIFICACAO[k]} ({porClassificacao[k]})
          </button>
        ))}
      </div>

      <div className="flex gap-2 text-xs text-slate-500">
        <label className="min-w-0 flex-1">
          Ordenar por
          <select
            value={ordem}
            onChange={(e) => setOrdem(e.target.value as Ordem)}
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-800"
          >
            <option value="nome">Nome</option>
            <option value="ultima_compra">Última compra</option>
            <option value="situacao">Situação</option>
          </select>
        </label>
      </div>

      <p className="text-sm font-semibold text-slate-800">Clientes encontrados ({visiveis.length})</p>

      <ul className="space-y-2">
        {visiveis.map(({ cliente: c, compras, situacao }) => {
          const nome = c.nome_fantasia || c.razao_social
          const ultima = compras[0]
          return (
            <li key={c.id}>
              <Link to={`/clientes/${c.id}`} className="flex items-center gap-3 rounded-xl bg-white p-3 shadow-sm">
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                    c.potencial ? FUNDO_CLASSIFICACAO[c.potencial] : 'bg-slate-100 text-slate-600'
                  }`}
                  title={c.potencial ? ROTULO_CLASSIFICACAO[c.potencial] : undefined}
                >
                  {c.potencial ? <span className="text-xl">{EMOJI_CLASSIFICACAO[c.potencial]}</span> : <span title="Sem classificação" className="text-lg">?</span>}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-slate-900">{nome}</span>
                  {(c.municipio || c.uf) && (
                    <span className="block truncate text-xs text-slate-500">
                      📍 {[c.municipio, c.uf].filter(Boolean).join(' - ')}
                    </span>
                  )}
                  {/* Inativo de cadastro tem selo proprio: a situacao de compra
                      dele nao diz nada, e "Inativo" so existe para o cadastro (F2). */}
                  {c.status === 'inativo' ? (
                    <span className="mt-1 inline-block rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                      Inativo
                    </span>
                  ) : (
                    <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${COR_SELO[situacao]}`}>
                      {ICONE_SELO[situacao]} {ROTULO_SITUACAO[situacao]}
                    </span>
                  )}
                </span>
                <span className="shrink-0 border-l border-slate-100 pl-3 text-right text-xs">
                  {ultima ? (
                    <>
                      <span className="block text-slate-500">Última compra</span>
                      <span className="block text-slate-800">{dataBR(ultima.data)}</span>
                      <span className="block font-semibold text-slate-800">{formatarMoeda(ultima.valor)}</span>
                    </>
                  ) : (
                    <span className="block text-slate-500">Nenhuma compra</span>
                  )}
                </span>
                <span className="text-slate-400" aria-hidden>›</span>
              </Link>
            </li>
          )
        })}
      </ul>
      {dados && visiveis.length === 0 && <p className="text-sm text-slate-500">Nenhum cliente encontrado.</p>}
    </section>
  )
}
